// This is the replacement for `src/rxdb/RxdbManager.ts`

import { RxDatabase } from 'rxdb';
import { RxGraphQLReplicationState } from 'rxdb/dist/types/plugins/replication-graphql';

import { BorerDatabaseCollections } from '@/models/BorerDatabaseCollections';
import { refreshTokenOnSyncState } from '@/rxdb/rxdbUtilityFunctions';
import { CheckpointType } from '@/rxdb/types';
import { KindOf } from '@/utilities/HasKind';

import { kind as destroy } from '../actions/goals/Destroy';
import { kind as disable } from '../actions/goals/Disable';
import { createGoal } from '../actions/goals/Goal';
import { Goals } from '../actions/goals/Goals';
import { kind as rebuild } from '../actions/goals/Rebuild';
import { kind as start } from '../actions/goals/Start';
import { kind as stop } from '../actions/goals/Stop';
import { toLegacyDB } from '../Database';
import { Dispatch } from '../Dispatch';
import { logError } from '../logError';
import { crumbleReplicationState } from '../replicate';
import { crumble, State } from '../State';
import { IRxdbManager } from './IRxdbManager';

export class LegacyRxdbAdapter implements IRxdbManager {
  private state: State;

  private dispatch: Dispatch;

  // WARNING: all of these public properties need to be stable
  // as they're regularly used as hook dependencies.
  public db: RxDatabase<BorerDatabaseCollections> | undefined;

  public isInitialized = false;

  // This is required by IRxdbManager.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public syncStates: RxGraphQLReplicationState<any, CheckpointType>[] = [];

  constructor(state: State, dispatch: Dispatch) {
    this.state = state;
    this.dispatch = dispatch;
    this.update(state, dispatch);
  }

  public update(state: State, dispatch: Dispatch) {
    this.state = state;
    this.dispatch = dispatch;
    this.db = state.database ? toLegacyDB(state.database) : undefined;
    this.isInitialized = !!this.db;
    this.syncStates = state.replication?.replicationStates || [];
  }

  public async startReplication(): Promise<void> {
    return this.dispatchGoal(start);
  }

  private async dispatchGoal(kind: KindOf<Goals>): Promise<void> {
    const goal = createGoal(kind);
    this.dispatch(goal);
    return goal.resolvers.promise;
  }

  public async stopReplication(preventRestart?: boolean | undefined): Promise<void> {
    if (preventRestart) {
      return this.dispatchGoal(disable);
    }
    return this.dispatchGoal(stop);
  }

  // This gets called before major actions like `Destroy` and `Rebuild`.
  // This should not be necessary under the state management of Replication V2,
  // but there are still calls to it scattered throughout the code,
  // so we'll preserve it for now.
  public async runSingleReplication(): Promise<void> {
    if (this.state.replication) {
      this.state.replication.replicationStates.map(state => {
        state.reSync(); // Not a promise
        return state.awaitInSync().catch(reason => {
          const message = 'Error waiting for state to be in sync during runSingleReplication.';
          // Since we're not going to be waiting for these to complete,
          // we'd better log problems with them.
          logError(message, reason, crumbleReplicationState(state));
        });
      });
      // BUG: this preserves the legacy behaviour of _not_ awaiting any async calls,
      // so even though it is an `async` function,
      // it returns (nearly) immediately regardless of what is happening in the sync.
      // If we were to await, it would almost certainly be:
      // await Promise.allSettled(promises);
    }
  }

  public async refreshTokensOnAllCollections(): Promise<void> {
    if (this.state.replication) {
      const promises = this.state.replication.replicationStates.map(state =>
        refreshTokenOnSyncState(state, 6 * 60 * 60),
      );
      // BUG: this preserves the legacy behaviour of rejecting when _any_ of the refreshes fail.
      // Consider switching to Promise.allSettled and logging individual failures instead.
      await Promise.all(promises);
    }
  }

  public destroyDB(): Promise<void> {
    return this.dispatchGoal(destroy);
  }

  public rebuildDb(): Promise<void> {
    return this.dispatchGoal(rebuild);
  }

  public async waitForIdle(): Promise<boolean> {
    // A lack of `whenIdle` is idle by definition.
    if (!this.state.whenIdle) return true;
    try {
      await this.state.whenIdle.promise;
      return true;
    } catch (error) {
      // We're not propagating any rejections/errors so we should be logging this here.
      logError('Error while waiting for state to idle', error, crumble(this.state));
      return false;
    }
  }
}
